import React from "react";
import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SxProps,
  Theme,
  Typography
} from "@mui/material";
import {
  Control,
  Controller,
  FieldErrors,
  UseFormSetValue,
  UseFormWatch
} from "react-hook-form";
import {
  CountryFullName,
  getCurrencyForCompanyCountry,
  getEmptyCompanyAddress,
  SUPPORTED_COMPANY_COUNTRIES,
  SupportedCurrencyCountryMap,
  translateCompanyCountry,
  useKivunovaTranslation
} from "@kivunova/kivufrontendcommon";
import RwandaCompanyAddressForm from "./RwandaCompanyAddressForm";
import {
  KenyaCompanyAddressForm,
  TanzaniaCompanyAddressForm,
  UgandaCompanyAddressForm
} from "./ManualCountryAddressForms";
import {
  getNestedFieldError,
  getNestedFieldErrors
} from "./companyAddressFormUtils";

interface CompanyCountryCurrencyAddressSectionProps {
  control: Control<Record<string, unknown>>;
  errors: FieldErrors;
  watch: UseFormWatch<Record<string, unknown>>;
  setValue: UseFormSetValue<Record<string, unknown>>;
  addressFieldPrefix: string;
  currencyFieldName: string;
  countryEditable: boolean;
  availableCountries?: string[];
  countrySelectLoading?: boolean;
  idPrefix?: string;
  selectSx?: SxProps<Theme>;
  inputSx?: SxProps<Theme>;
  size?: "small" | "medium";
}

const CompanyCountryCurrencyAddressSection: React.FC<
  CompanyCountryCurrencyAddressSectionProps
> = ({
  control,
  errors,
  watch,
  setValue,
  addressFieldPrefix,
  currencyFieldName,
  countryEditable,
  availableCountries = SUPPORTED_COMPANY_COUNTRIES,
  countrySelectLoading = false,
  idPrefix = "",
  selectSx,
  inputSx,
  size = "medium"
}) => {
  const { t } = useKivunovaTranslation();
  const selectedCountry = watch(`${addressFieldPrefix}.country`) as
    string | undefined;
  const currencyError = getNestedFieldError(errors, currencyFieldName);
  const resolvedAddressErrors = getNestedFieldErrors(
    errors,
    addressFieldPrefix
  );

  const currencyOptions = selectedCountry
    ? Object.values(SupportedCurrencyCountryMap).filter(
        (c) => c.code === getCurrencyForCompanyCountry(selectedCountry)
      )
    : [];

  React.useEffect(() => {
    if (!selectedCountry) return;
    const expectedCurrency = getCurrencyForCompanyCountry(selectedCountry);
    if (expectedCurrency) {
      setValue(currencyFieldName as never, expectedCurrency as never);
    }
  }, [selectedCountry, setValue, currencyFieldName]);

  const handleCountryChange = (country: string) => {
    setValue(
      addressFieldPrefix as never,
      getEmptyCompanyAddress(country) as never
    );
    const expectedCurrency = getCurrencyForCompanyCountry(country);
    if (expectedCurrency) {
      setValue(currencyFieldName as never, expectedCurrency as never);
    }
  };

  const renderAddressForm = () => {
    switch (selectedCountry) {
      case CountryFullName.RW:
        return (
          <RwandaCompanyAddressForm
            control={control}
            errors={errors}
            watch={watch}
            setValue={setValue}
            fieldPrefix={addressFieldPrefix}
            idPrefix={idPrefix}
            selectSx={selectSx}
            inputSx={inputSx}
            size={size}
          />
        );
      case CountryFullName.UG:
        return (
          <UgandaCompanyAddressForm
            control={control}
            errors={errors}
            watch={watch}
            setValue={setValue}
            fieldPrefix={addressFieldPrefix}
            idPrefix={idPrefix}
            inputSx={inputSx}
            size={size}
          />
        );
      case CountryFullName.KE:
        return (
          <KenyaCompanyAddressForm
            control={control}
            errors={errors}
            watch={watch}
            setValue={setValue}
            fieldPrefix={addressFieldPrefix}
            idPrefix={idPrefix}
            inputSx={inputSx}
            size={size}
          />
        );
      case CountryFullName.TZ:
        return (
          <TanzaniaCompanyAddressForm
            control={control}
            errors={errors}
            watch={watch}
            setValue={setValue}
            fieldPrefix={addressFieldPrefix}
            idPrefix={idPrefix}
            inputSx={inputSx}
            size={size}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Controller
        name={currencyFieldName}
        control={control}
        render={({ field }) => (
          <FormControl fullWidth size={size}>
            <InputLabel shrink id={`${idPrefix}currency-code-select-label`}>
              {t("currency-code-label-text", "Currency code")}
            </InputLabel>
            <Select
              {...field}
              required
              labelId={`${idPrefix}currency-code-select-label`}
              id={`${idPrefix}currency-code-select`}
              label={t("currency-code", "Currency Code")}
              error={!!currencyError}
              disabled={!selectedCountry}
              inputProps={
                idPrefix
                  ? { "data-testid": `${idPrefix}currencyCode` }
                  : undefined
              }
              sx={selectSx}
            >
              {currencyOptions.map((c) => (
                <MenuItem
                  key={c.code}
                  value={c.code}
                  id={`${idPrefix}currencyCode-${c.code}`}
                >
                  {t(c.labelTranslationKey, c.label)}
                </MenuItem>
              ))}
            </Select>
            {currencyError?.message && (
              <FormHelperText error>
                {String(currencyError.message)}
              </FormHelperText>
            )}
          </FormControl>
        )}
      />

      <Paper
        variant="outlined"
        sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}
      >
        <Typography variant="subtitle1">
          {t("address-label", "Address")}
        </Typography>

        <Controller
          name={`${addressFieldPrefix}.country`}
          control={control}
          render={({ field }) => (
            <FormControl fullWidth size={size}>
              <InputLabel shrink id={`${idPrefix}country-select-label`}>
                {t("country", "Country")}
              </InputLabel>
              <Select
                {...field}
                required
                labelId={`${idPrefix}country-select-label`}
                id={`${idPrefix}country-selector`}
                label={t("country", "Country")}
                disabled={!countryEditable || countrySelectLoading}
                displayEmpty={countrySelectLoading}
                error={!!(resolvedAddressErrors as FieldErrors)?.country}
                renderValue={
                  countrySelectLoading
                    ? () =>
                        t("registration-country-loading", "Loading countries…")
                    : undefined
                }
                onChange={(e) => {
                  field.onChange(e);
                  if (countryEditable && e.target.value) {
                    handleCountryChange(e.target.value);
                  }
                }}
                inputProps={
                  idPrefix ? { "data-testid": `${idPrefix}country` } : undefined
                }
                sx={selectSx}
              >
                {availableCountries.map((c) => (
                  <MenuItem key={c} value={c} id={`${idPrefix}country-${c}`}>
                    {translateCompanyCountry(c, t)}
                  </MenuItem>
                ))}
              </Select>
              {(resolvedAddressErrors as FieldErrors)?.country?.message && (
                <FormHelperText error>
                  {String(
                    (resolvedAddressErrors as FieldErrors).country?.message
                  )}
                </FormHelperText>
              )}
            </FormControl>
          )}
        />

        {selectedCountry && renderAddressForm()}
      </Paper>
    </>
  );
};

export default CompanyCountryCurrencyAddressSection;
